import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ReplayIcon from '@mui/icons-material/Replay';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import SectionTitle from 'components/ui/typography/SectionTitle';
import { TransportDocumentActiveStatus } from 'features/inventory/transport-documents/TransportActiveDocument/constants';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { StatusOption } from 'shared/types/general';
import { dateCell, DataGridLinkCell, baseCell } from 'components/cells';
import {
  dateFilterOperators,
  filterOperators,
  singleSelectFilterOperators
} from 'components/enhanced';

export function TransportActiveDocumentColumns(
  isLinkable: boolean,
  downloadAttachment: any,
  regenerateDocument: any,
  isSupplierDdt?: boolean
): GridColDef[] {
  const { t } = useTranslation();

  const statusOptions: StatusOption[] = Object.entries(TransportDocumentActiveStatus).map(
    ([display_name, key]) => {
      return {
        value: Number(key),
        display_name: String(display_name)
      };
    }
  );

  const columns: GridColDef[] = [
    {
      field: 'ref',
      headerName: t(isSupplierDdt ? `entities.supplier-active-ddt.ref` : `entities.ddt.ref`),
      width: 175,
      renderCell: (params: GridRenderCellParams<any>) =>
        isLinkable ? (
          <NavLink to={`./${params.row?.id}`}>
            <SectionTitle>{params.value}</SectionTitle>
          </NavLink>
        ) : (
          <SectionTitle>{params.value}</SectionTitle>
        )
    },
    {
      field: 'date',
      headerName: t(isSupplierDdt ? `entities.supplier-active-ddt.date` : `entities.ddt.date`),
      filterOperators: dateFilterOperators,
      width: 125,
      renderCell: dateCell
    },
    {
      field: 'order_ref',
      headerName: t(`global.order`),
      filterOperators,
      width: 125,
      renderCell: useIsTenantAdministrator()
        ? (params) => (
            <DataGridLinkCell params={params} basePath="tenant/orders/view" idKey="order" />
          )
        : baseCell
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      width: 100,
      type: 'singleSelect',
      filterOperators: singleSelectFilterOperators,
      valueOptions: statusOptions.map((opt) => {
        return {
          value: String(opt.value),
          label: opt.display_name
        };
      }),
      renderCell: ({ value }) => (
        <span className={value === 'ACTIVE' ? 'text-green-500' : 'text-orange-500'}>
          {t(`entities.active-ddt.status.${value}`)}
        </span>
      )
    },
    {
      field: 'warehouse_out',
      headerName: t(`global.warehouse_out`),
      filterOperators,
      width: 200,
      renderCell: baseCell
    }
  ];

  if (isSupplierDdt) {
    columns.push({
      field: 'supplier',
      headerName: t(`global.supplier`),
      filterOperators,
      minWidth: 200,
      flex: 1,
      renderCell: baseCell
    });
  } else {
    columns.push({
      field: 'warehouse_in',
      headerName: t(`global.warehouse_in`),
      filterOperators,
      minWidth: 300,
      flex: 1,
      renderCell: baseCell
    });
  }

  columns.push({
    field: 'actions',
    type: 'actions',
    headerName: t(`global.actions`),
    getActions: (params: GridRowParams) => {
      return [
        {
          label: 'Scarica DDT',
          icon: <CloudDownloadIcon />,
          onClick: () => downloadAttachment(params.row?.id),
          visible: Boolean(params.row.ddt_file)
        },
        {
          label: 'Rigenera PDF',
          icon: <ReplayIcon />,
          onClick: () => regenerateDocument(params.row?.id)
        }
      ];
    }
  } as GridColDef);

  return columns;
}
